/**
 * Whether the GIF provider can be searched at all (R4).
 *
 * The composer needs this before the sheet ever opens, because R4 disables
 * the GIF action rather than hiding it. There is no separate health
 * endpoint, so this probes `/gif/search` with the same trending query the
 * picker opens on: the react-query key is identical, so opening the sheet
 * costs no second network call, and `searchGifs.ts` already answers
 * `isProviderAvailable: false` instead of throwing whenever the key is
 * missing, the quota is spent, or the call times out.
 *
 * This runs on every conversation open, for every fan, whether or not they
 * ever tap the GIF action, because R4 needs the answer before the sheet
 * exists. `GIF_SEARCH_STALE_TIME_MS` is what keeps that from becoming a
 * provider search every half minute per client.
 */
import { useGifSearch } from "@sportsmet/messaging-sdk/private";

import {
  GIF_SEARCH_STALE_TIME_MS,
  GIF_TRENDING_QUERY,
  readGifSearchResponse,
} from "./gifPickerLogic";

export type typeGifAvailability = {
  isProviderAvailable: boolean;
  isPending: boolean;
};

export function useGifAvailability(): typeGifAvailability {
  const query = useGifSearch(
    { query: GIF_TRENDING_QUERY, cursor: null },
    { staleTime: GIF_SEARCH_STALE_TIME_MS },
  );
  const reading = readGifSearchResponse(query.data, query.isError);
  return {
    isProviderAvailable: reading.isProviderAvailable,
    isPending: query.isPending,
  };
}

/**
 * R4's rule for the composer button, kept here so the button and its test
 * read the same sentence.
 *
 * Disabled only on a settled negative answer. While the probe is still in
 * flight nothing is known yet, and starting out disabled would make the
 * action flicker from dimmed to live on every conversation open, which
 * reads as a broken control rather than an honest one.
 */
export function isGifActionDisabled(
  availability: typeGifAvailability,
): boolean {
  return !availability.isPending && !availability.isProviderAvailable;
}
